import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { SearchIcon } from '@/components/layout/icons';
import { X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/cn';
import { StatusBadge } from '@/features/client/web/chat/status-badge';
import { api, apiErrorMessage } from '@shared/api/client';
import { acceptAssignment, declineAssignment } from '@shared/api/transactions';
import { useMyTransactions } from '@shared/hooks/use-transactions';
import { useMe } from '@shared/hooks/use-me';
import type { TransactionDetail, TransactionListItem, Uuid } from '@shared/types/api';
import { DocumentsDialog } from './documents-dialog';
import { KycStatusCard } from './kyc-status-card';
import { Pagination } from '@/components/ui/pagination';

type OpenDialog = { kind: 'docs'; txId: Uuid } | null;
type SectionFilter = 'active' | 'completed' | 'cancelled' | 'declined' | 'all';

export const ASSIGNMENT_OFFER_DURATION_MS = 2 * 60 * 60 * 1000; // 2 hours

export function TransactionsPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  const getInitialTab = (): 'assigned' | 'taken' => {
    const tabParam = searchParams.get('tab');
    if (tabParam === 'taken' || tabParam === 'assigned') {
      return tabParam;
    }
    const saved = sessionStorage.getItem('associate_transactions_tab');
    if (saved === 'taken' || saved === 'assigned') {
      return saved;
    }
    return 'assigned';
  };

  const [activeTab, setActiveTabState] = useState<'assigned' | 'taken'>(getInitialTab);
  const [sectionFilter, setSectionFilter] = useState<SectionFilter>('active');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQ(q);
      setPage(1);
    }, 200);
    return () => clearTimeout(timer);
  }, [q]);

  const handleTabChange = (tab: 'assigned' | 'taken') => {
    setActiveTabState(tab);
    sessionStorage.setItem('associate_transactions_tab', tab);
    setSearchParams({ tab }, { replace: true });
    setSectionFilter('active');
    setPage(1);
  };

  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam === 'taken' || tabParam === 'assigned') {
      if (tabParam !== activeTab) {
        setActiveTabState(tabParam);
        sessionStorage.setItem('associate_transactions_tab', tabParam);
      }
    } else {
      const saved = sessionStorage.getItem('associate_transactions_tab');
      if (saved === 'taken' || saved === 'assigned') {
        setSearchParams({ tab: saved }, { replace: true });
        if (saved !== activeTab) {
          setActiveTabState(saved);
        }
      }
    }
  }, [searchParams, setSearchParams]);

  const { data, isLoading } = useMyTransactions({
    scope: activeTab === 'assigned' ? 'associate' : 'client',
    filter: sectionFilter,
    q: debouncedQ.trim() || undefined,
    page,
    pageSize,
  });
  const isInitialLoading = isLoading && !data;
  const [dialog, setDialog] = useState<OpenDialog>(null);

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = data?.totalPages ?? 1;
  const counts = data?.counts ?? { active: 0, completed: 0, cancelled: 0, declined: 0, all: 0 };

  return (
    <div className="w-full px-4 py-6 sm:px-6 sm:py-8">
      <h1 className="text-xl font-semibold text-gray-900">Transactions</h1>
      <p className="text-sm text-gray-600">
        {activeTab === 'assigned'
          ? 'View, manage, and act on assigned client transactions.'
          : 'Track and manage services you have requested as a client.'}
      </p>

      {/* Switcher Chips (Assigned Services / Taken Services) */}
      <div className="mt-5 flex items-center gap-2">
        <button
          type="button"
          onClick={() => handleTabChange('assigned')}
          className={cn(
            'px-4 py-1.5 rounded-full text-xs font-semibold tracking-wide uppercase transition-all shadow-xs border cursor-pointer',
            activeTab === 'assigned'
              ? 'bg-brand-700 border-brand-700 text-white font-bold'
              : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50',
          )}
        >
          Assigned Services
        </button>
        <button
          type="button"
          onClick={() => handleTabChange('taken')}
          className={cn(
            'px-4 py-1.5 rounded-full text-xs font-semibold tracking-wide uppercase transition-all shadow-xs border cursor-pointer',
            activeTab === 'taken'
              ? 'bg-brand-700 border-brand-700 text-white font-bold'
              : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50',
          )}
        >
          Taken Services
        </button>
      </div>

      {activeTab === 'assigned' && (
        <div className="mt-5">
          <KycStatusCard />
        </div>
      )}

      {/* Section Filter Bar (Active / Completed / Cancelled / Declined / All) */}
      <div className="mt-5 flex flex-wrap items-center gap-2 border-b border-gray-200 pb-3">
        <button
          type="button"
          onClick={() => {
            setSectionFilter('active');
            setPage(1);
          }}
          className={cn(
            'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer',
            sectionFilter === 'active'
              ? 'bg-brand-50 text-brand-800 ring-1 ring-brand-600/30 font-bold'
              : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900',
          )}
        >
          <span>Active</span>
          <span
            className={cn(
              'px-1.5 py-0.5 rounded-full text-[10px] font-bold',
              sectionFilter === 'active' ? 'bg-brand-700 text-white' : 'bg-gray-100 text-gray-600',
            )}
          >
            {counts.active}
          </span>
        </button>

        <button
          type="button"
          onClick={() => {
            setSectionFilter('completed');
            setPage(1);
          }}
          className={cn(
            'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer',
            sectionFilter === 'completed'
              ? 'bg-emerald-50 text-emerald-800 ring-1 ring-emerald-600/30 font-bold'
              : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900',
          )}
        >
          <span>Completed</span>
          <span
            className={cn(
              'px-1.5 py-0.5 rounded-full text-[10px] font-bold',
              sectionFilter === 'completed'
                ? 'bg-emerald-600 text-white'
                : 'bg-gray-100 text-gray-600',
            )}
          >
            {counts.completed}
          </span>
        </button>

        <button
          type="button"
          onClick={() => {
            setSectionFilter('cancelled');
            setPage(1);
          }}
          className={cn(
            'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer',
            sectionFilter === 'cancelled'
              ? 'bg-red-50 text-red-800 ring-1 ring-red-600/30 font-bold'
              : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900',
          )}
        >
          <span>Cancelled</span>
          <span
            className={cn(
              'px-1.5 py-0.5 rounded-full text-[10px] font-bold',
              sectionFilter === 'cancelled' ? 'bg-red-600 text-white' : 'bg-gray-100 text-gray-600',
            )}
          >
            {counts.cancelled}
          </span>
        </button>

        {activeTab === 'assigned' && (
          <button
            type="button"
            onClick={() => {
              setSectionFilter('declined');
              setPage(1);
            }}
            className={cn(
              'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer',
              sectionFilter === 'declined'
                ? 'bg-amber-50 text-amber-900 ring-1 ring-amber-600/30 font-bold'
                : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900',
            )}
          >
            <span>Declined</span>
            <span
              className={cn(
                'px-1.5 py-0.5 rounded-full text-[10px] font-bold',
                sectionFilter === 'declined'
                  ? 'bg-amber-700 text-white'
                  : 'bg-gray-100 text-gray-600',
              )}
            >
              {counts.declined}
            </span>
          </button>
        )}

        <button
          type="button"
          onClick={() => {
            setSectionFilter('all');
            setPage(1);
          }}
          className={cn(
            'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer',
            sectionFilter === 'all'
              ? 'bg-brand-50 text-brand-800 ring-1 ring-brand-600/30 font-bold'
              : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900',
          )}
        >
          <span>All</span>
          <span
            className={cn(
              'px-1.5 py-0.5 rounded-full text-[10px] font-bold',
              sectionFilter === 'all' ? 'bg-brand-700 text-white' : 'bg-gray-100 text-gray-600',
            )}
          >
            {counts.all}
          </span>
        </button>
      </div>

      <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
        <div className="relative flex-1 max-w-md">
          <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by code, service, client name, mobile..."
            className="pl-9 pr-8"
          />
          {q && (
            <button
              type="button"
              onClick={() => {
                setQ('');
                setDebouncedQ('');
                setPage(1);
              }}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-0.5 rounded cursor-pointer"
              title="Clear search"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>

        <Pagination
          page={page}
          pageSize={pageSize}
          total={total}
          totalPages={totalPages}
          onPageChange={setPage}
          onPageSizeChange={(newSize) => {
            setPageSize(newSize);
            setPage(1);
          }}
          isLoading={isInitialLoading}
          showBorder={false}
          showRange={false}
          showPageSize={false}
          compact
        />
      </div>

      <section className="mt-5">
        {/* Mobile Cards List */}
        <div className="space-y-3 md:hidden">
          {isInitialLoading ? (
            <p className="p-6 text-center text-sm text-gray-500 bg-white rounded-lg border border-gray-200">
              Loading…
            </p>
          ) : items.length === 0 ? (
            <p className="p-6 text-center text-sm text-gray-500 bg-white rounded-lg border border-gray-200">
              No transactions found for section &quot;{sectionFilter}&quot;.
            </p>
          ) : (
            items.map((t) => (
              <TransactionCard
                key={t.id}
                tx={t}
                showActions={true}
                onViewDocs={() => setDialog({ kind: 'docs', txId: t.id })}
              />
            ))
          )}
        </div>

        {/* Desktop Table */}
        <div className="hidden md:block overflow-x-auto rounded-lg border border-gray-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-4 py-3 text-left font-medium">Service ID</th>
                <th className="px-4 py-3 text-left font-medium">Service / Sub-service</th>
                <th className="px-4 py-3 text-left font-medium">Status</th>
                <th className="px-4 py-3 text-left font-medium">Documents</th>
                <th className="px-4 py-3 text-center font-medium">Chat</th>
                <th className="px-4 py-3 text-right font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isInitialLoading ? (
                <tr>
                  <td colSpan={6} className="p-6 text-center text-gray-500">
                    Loading…
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={6} className="p-6 text-center text-gray-500">
                    No transactions found for section &quot;{sectionFilter}&quot;.
                  </td>
                </tr>
              ) : (
                items.map((t) => (
                  <TransactionRow
                    key={t.id}
                    tx={t}
                    showActions={true}
                    onViewDocs={() => setDialog({ kind: 'docs', txId: t.id })}
                  />
                ))
              )}
            </tbody>
          </table>

          <Pagination
            page={page}
            pageSize={pageSize}
            total={total}
            totalPages={totalPages}
            onPageChange={setPage}
            onPageSizeChange={(newSize) => {
              setPageSize(newSize);
              setPage(1);
            }}
            isLoading={isInitialLoading}
          />
        </div>

        {/* Mobile Pagination */}
        <div className="md:hidden mt-4 rounded-lg border border-gray-200 bg-white overflow-hidden">
          <Pagination
            page={page}
            pageSize={pageSize}
            total={total}
            totalPages={totalPages}
            onPageChange={setPage}
            onPageSizeChange={(newSize) => {
              setPageSize(newSize);
              setPage(1);
            }}
            isLoading={isInitialLoading}
            showBorder={false}
          />
        </div>
      </section>

      {dialog?.kind === 'docs' ? (
        <DocumentsDialog open onClose={() => setDialog(null)} transactionId={dialog.txId} />
      ) : null}
    </div>
  );
}

function TransactionCard({
  tx,
  onViewDocs,
  showActions = true,
}: {
  tx: TransactionListItem;
  onViewDocs: () => void;
  showActions?: boolean;
}) {
  const { data: me } = useMe();
  const isClientForTx = tx.clientId === me?.id;
  const isMyAccepted =
    tx.assignedAssociateId === me?.id && tx.associateAssignmentStatus === 'ACCEPTED';
  const isPendingCandidate = tx.candidateAssociateIds?.includes(me?.id ?? '') && !isMyAccepted;
  const myCandidate = tx.candidateAssociates?.find((c) => c.id === me?.id);
  const myAssignedAtStr =
    myCandidate?.assignedAt ?? (me?.id ? tx.candidateAssignedAt?.[me.id] : null) ?? tx.assignedAt;
  const assignedAtTime = myAssignedAtStr ? new Date(myAssignedAtStr).getTime() : 0;
  const isCandidateNotAssigned =
    (tx.candidateAssociateIds?.includes(me?.id ?? '') ?? false) &&
    tx.assignedAssociateId !== me?.id;
  const isCardExpired =
    !isClientForTx &&
    !isMyAccepted &&
    (tx.isAssignmentExpired ||
      (tx.associateAssignmentStatus as string) === 'EXPIRED' ||
      (isCandidateNotAssigned && tx.assignedAssociateId !== null) ||
      (assignedAtTime > 0 && Date.now() >= assignedAtTime + ASSIGNMENT_OFFER_DURATION_MS));
  const canOpenChat = isClientForTx || isMyAccepted;

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="font-mono text-[10px] text-gray-500">#{tx.code}</div>
          <h3 className="font-semibold text-gray-900 mt-0.5 text-sm">{tx.service.name}</h3>
          {tx.subService?.name && (
            <p className="text-xs text-gray-500 mt-0.5">{tx.subService.name}</p>
          )}
        </div>
        <div className="shrink-0">
          <StatusBadge
            status={tx.status}
            assignmentStatus={tx.associateAssignmentStatus}
            isExpired={isCardExpired}
          />
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between border-t border-gray-100 pt-3 gap-2">
        <div className="flex gap-2">
          <button
            onClick={onViewDocs}
            className="rounded-md border border-gray-200 bg-white px-2.5 py-1 text-xs text-gray-700 hover:bg-gray-50 cursor-pointer"
          >
            Documents
          </button>
          {!canOpenChat ? (
            <span
              className="relative flex items-center justify-center rounded-md border border-gray-200 bg-gray-100/70 px-2.5 py-1 text-xs text-gray-400 cursor-not-allowed gap-1.5 font-medium"
              title={
                isPendingCandidate
                  ? 'Accept assignment to open chat'
                  : 'Chat disabled for declined or unassigned transaction'
              }
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              </svg>
              <span>Chat</span>
            </span>
          ) : (
            <Link
              to={`/associate/chat/${tx.id}`}
              className="relative flex items-center justify-center rounded-md border border-gray-200 px-2.5 py-1 text-xs text-gray-700 hover:bg-gray-50 gap-1.5"
              aria-label="Open chat"
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              </svg>
              <span>Chat</span>
              {tx.unreadCount > 0 && (
                <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-brand-700 px-1 text-[9px] font-semibold text-white">
                  {tx.unreadCount > 9 ? '9+' : tx.unreadCount}
                </span>
              )}
            </Link>
          )}
        </div>

        {showActions && <OuterActionBar tx={tx} />}
      </div>
    </div>
  );
}

function TransactionRow({
  tx,
  onViewDocs,
}: {
  tx: TransactionListItem;
  onViewDocs: () => void;
  showActions?: boolean;
}) {
  const { data: me } = useMe();
  const isClientForTx = tx.clientId === me?.id;
  const isMyAccepted =
    tx.assignedAssociateId === me?.id && tx.associateAssignmentStatus === 'ACCEPTED';
  const isPendingCandidate = tx.candidateAssociateIds?.includes(me?.id ?? '') && !isMyAccepted;
  const myCandidate = tx.candidateAssociates?.find((c) => c.id === me?.id);
  const myAssignedAtStr =
    myCandidate?.assignedAt ?? (me?.id ? tx.candidateAssignedAt?.[me.id] : null) ?? tx.assignedAt;
  const assignedAtTime = myAssignedAtStr ? new Date(myAssignedAtStr).getTime() : 0;
  const isCandidateNotAssigned =
    (tx.candidateAssociateIds?.includes(me?.id ?? '') ?? false) &&
    tx.assignedAssociateId !== me?.id;
  const isRowExpired =
    !isClientForTx &&
    !isMyAccepted &&
    (tx.isAssignmentExpired ||
      (tx.associateAssignmentStatus as string) === 'EXPIRED' ||
      (isCandidateNotAssigned && tx.assignedAssociateId !== null) ||
      (assignedAtTime > 0 && Date.now() >= assignedAtTime + ASSIGNMENT_OFFER_DURATION_MS));
  const canOpenChat = isClientForTx || isMyAccepted;

  return (
    <tr className="hover:bg-gray-50">
      <td className="px-4 py-3 align-top font-mono text-xs text-gray-900">{tx.code}</td>
      <td className="px-4 py-3 align-top">
        <div className="font-medium text-gray-900">{tx.service.name}</div>
        {tx.subService?.name ? (
          <div className="text-xs text-gray-500">{tx.subService.name}</div>
        ) : null}
      </td>
      <td className="px-4 py-3 align-top">
        <StatusBadge
          status={tx.status}
          assignmentStatus={tx.associateAssignmentStatus}
          isExpired={isRowExpired}
        />
      </td>
      <td className="px-4 py-3 align-top">
        <button
          onClick={onViewDocs}
          className="rounded-md border border-gray-200 bg-white px-2.5 py-1 text-xs text-gray-700 hover:bg-gray-50 cursor-pointer"
        >
          View Documents
        </button>
      </td>
      <td className="px-4 py-3 text-center align-top">
        {!canOpenChat ? (
          <span
            className="relative inline-flex items-center justify-center rounded-md p-1.5 text-gray-400 bg-gray-100/80 cursor-not-allowed border border-gray-200/80"
            title={
              isPendingCandidate
                ? 'Accept assignment to open chat'
                : 'Chat disabled for declined or unassigned transaction'
            }
            aria-label="Chat disabled"
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
          </span>
        ) : (
          <Link
            to={`/associate/chat/${tx.id}`}
            className="relative inline-flex rounded-full p-1 text-gray-600 hover:bg-gray-100 hover:text-gray-900 transition-colors"
            aria-label="Open chat"
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
            {tx.unreadCount > 0 ? (
              <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-brand-700 px-1 text-[10px] font-semibold text-white">
                {tx.unreadCount > 9 ? '9+' : tx.unreadCount}
              </span>
            ) : null}
          </Link>
        )}
      </td>
      <td className="px-4 py-3 align-top">
        <OuterActionBar tx={tx} />
      </td>
    </tr>
  );
}

function OuterActionBar({ tx }: { tx: TransactionListItem }) {
  const { data: me } = useMe();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const base = `/transactions/${tx.id}`;

  function invalidate() {
    qc.invalidateQueries({ queryKey: ['transactions'] });
    qc.invalidateQueries({ queryKey: ['transaction', tx.id] });
  }

  function invalidateAndRedirect() {
    invalidate();
    navigate(`/associate/chat/${tx.id}`);
  }

  const start = useMutation({
    mutationFn: () => api.post<TransactionDetail>(`${base}/start`).then((r) => r.data),
    onSuccess: invalidateAndRedirect,
  });

  const accept = useMutation({
    mutationFn: () => acceptAssignment(tx.id),
    onSuccess: invalidate,
  });

  const decline = useMutation({
    mutationFn: () => declineAssignment(tx.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['transactions'] });
      qc.invalidateQueries({ queryKey: ['transaction', tx.id] });
    },
  });

  const err = start.error ?? accept.error ?? decline.error;
  const s = tx.status;
  const pending = start.isPending || accept.isPending || decline.isPending;

  const isClientForTx = tx.clientId === me?.id;
  const isMyAccepted =
    tx.assignedAssociateId === me?.id && tx.associateAssignmentStatus === 'ACCEPTED';
  const isCandidate = tx.candidateAssociateIds?.includes(me?.id ?? '') && !isClientForTx;
  const isDeclined = tx.associateAssignmentStatus === 'DECLINED';

  const myCandidate = tx.candidateAssociates?.find((c) => c.id === me?.id);
  const myAssignedAtStr =
    myCandidate?.assignedAt ?? (me?.id ? tx.candidateAssignedAt?.[me.id] : null) ?? tx.assignedAt;
  const assignedAtTime = myAssignedAtStr ? new Date(myAssignedAtStr).getTime() : 0;
  const expiresAtTime = assignedAtTime > 0 ? assignedAtTime + ASSIGNMENT_OFFER_DURATION_MS : 0;
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!expiresAtTime || isMyAccepted || isDeclined) return;
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, [expiresAtTime, isMyAccepted, isDeclined]);

  const remainingSeconds =
    expiresAtTime > 0 ? Math.max(0, Math.ceil((expiresAtTime - now) / 1000)) : 0;
  const isOfferExpired =
    isCandidate &&
    !isMyAccepted &&
    (tx.isAssignmentExpired || (expiresAtTime > 0 && remainingSeconds <= 0));

  const actions: React.ReactNode[] = [];

  if (isClientForTx) {
    actions.push(
      <Button
        key="open"
        className="h-7 px-3 py-0.5 text-xs bg-brand-700 hover:bg-brand-800 text-white font-medium shadow-xs cursor-pointer"
        onClick={() => navigate(`/associate/chat/${tx.id}`)}
      >
        Open
      </Button>,
    );
  } else if (isCandidate && !isMyAccepted && !isDeclined) {
    if (isOfferExpired) {
      actions.push(
        <span
          key="expired"
          className="inline-flex items-center gap-1 rounded bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-800 border border-amber-200"
          title="2-hour offer timer reached 00:00:00. Admin can re-assign."
        >
          <svg
            className="h-3.5 w-3.5 text-amber-600 shrink-0"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          Offer Expired (00:00)
        </span>,
      );
    } else {
      const hours = Math.floor(remainingSeconds / 3600);
      const mins = Math.floor((remainingSeconds % 3600) / 60);
      const secs = remainingSeconds % 60;
      const formattedTimer =
        hours > 0
          ? `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`
          : `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
      actions.push(
        <span
          key="timer"
          className="inline-flex items-center gap-1 rounded bg-emerald-50 px-2 py-0.5 text-xs font-mono font-medium text-emerald-800 border border-emerald-200"
        >
          <svg
            className="h-3 w-3 text-emerald-600 animate-pulse shrink-0"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          {formattedTimer}
        </span>,
        <Button
          key="accept"
          className="h-7 px-2.5 py-0.5 text-xs bg-emerald-600 hover:bg-emerald-700 text-white font-medium shadow-xs cursor-pointer"
          onClick={() => accept.mutate()}
          disabled={pending}
        >
          {accept.isPending ? 'Accepting…' : 'Accept'}
        </Button>,
        <Button
          key="decline"
          variant="outline"
          className="h-7 px-2.5 py-0.5 text-xs text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700 font-medium bg-white shadow-xs cursor-pointer"
          onClick={() => decline.mutate()}
          disabled={pending}
        >
          {decline.isPending ? 'Declining…' : 'Decline'}
        </Button>,
      );
    }
  } else if (isMyAccepted) {
    if (s === 'ASSIGNED' || s === 'REVISION_REQUESTED') {
      actions.push(
        <Button
          key="start"
          className="h-7 px-3 py-0.5 text-xs bg-emerald-600 hover:bg-emerald-700 text-white font-medium shadow-xs cursor-pointer"
          onClick={() => start.mutate()}
          disabled={pending}
        >
          {start.isPending ? 'Starting…' : 'Start work'}
        </Button>,
      );
    }
    actions.push(
      <Button
        key="open"
        className="h-7 px-3 py-0.5 text-xs bg-brand-700 hover:bg-brand-800 text-white font-medium shadow-xs cursor-pointer"
        onClick={() => navigate(`/associate/chat/${tx.id}`)}
      >
        Open
      </Button>,
    );
  }

  if (actions.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-col items-end gap-1">
      <div className="flex flex-wrap items-center justify-end gap-1.5">{actions}</div>
      {err ? <span className="text-[11px] text-red-600">{apiErrorMessage(err)}</span> : null}
    </div>
  );
}
